import Phaser from 'phaser';
import { GameScene } from '../GameScene';

/**
 * Controlador del HUD in-game (globos de HP/mana, barra de XP, botones de paneles, keycaps),
 * extraído de GameScene (god-object). Posee los ~44 objetos gráficos del HUD y su escala; los
 * helpers de render de globos/líquido siguen en GameScene y se acceden vía `this.scene.*`.
 * Ver docs/UI_SKINNING.md. Accede a la escena vía `scene`.
 */
export class HUDController {
  public hudScale: number = GameScene.HUD_SCALE_VALUES[1]; // x1 (valor 2.0, base por defecto)
  public hudElements: Phaser.GameObjects.Image[] = [];
  public hudHpGlobeFill: Phaser.GameObjects.Image | null = null;
  public hudHpGlobeFillH = 0;
  public hudManaGlobeFill: Phaser.GameObjects.Image | null = null;
  public hudManaGlobeFillH = 0;
  public hudManaReserveGlobe: Phaser.GameObjects.Image | null = null;
  public hudManaReserveGlobeH = 0;
  public hudManaReserveLabel: Phaser.GameObjects.Text | null = null;
  public hudHpGlobeEmpty: Phaser.GameObjects.Image | null = null;
  public hudManaGlobeEmpty: Phaser.GameObjects.Image | null = null;
  public hudHpGlobeLayers: Phaser.GameObjects.Image[] = [];
  public hudManaGlobeLayers: Phaser.GameObjects.Image[] = [];
  public hudHpGlobeBl: Phaser.GameObjects.Image | null = null;
  public hudManaGlobeBl: Phaser.GameObjects.Image | null = null;
  public hudHpGlobeFx: Phaser.GameObjects.Image | null = null;
  public hudManaGlobeFx: Phaser.GameObjects.Image | null = null;
  public hudHpBubbles: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  public hudManaBubbles: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  public hudHpBubbleZone: Phaser.Geom.Circle | null = null;
  public hudManaBubbleZone: Phaser.Geom.Circle | null = null;
  public hudHpLiquidMask: Phaser.GameObjects.Graphics | null = null;
  public hudManaLiquidMask: Phaser.GameObjects.Graphics | null = null;
  public hudHpOrbCenter: { x: number; y: number } = { x: 0, y: 0 };
  public hudManaOrbCenter: { x: number; y: number } = { x: 0, y: 0 };
  public hudOrbRadius = 0;
  public hudHpFillFrac = 0;
  public hudManaFillFrac = 0;
  public hudHpLabel: Phaser.GameObjects.Text | null = null;
  public hudManaLabel: Phaser.GameObjects.Text | null = null;
  public hudPctText: Phaser.GameObjects.Text | null = null;
  public hudPctShowRaw = false; // false = %; true = actual/siguiente nivel
  public hudExpBar: Phaser.GameObjects.Image | null = null;
  public hudExpBarOriginalWidth: number = 0;
  public hudMainFrame: Phaser.GameObjects.Image | null = null;
  public hudXpFrame: Phaser.GameObjects.Image | null = null;
  public hudExtraKeycaps: Phaser.GameObjects.Image[] = [];
  public hudExtraKeyLabels: Phaser.GameObjects.Text[] = [];
  public hudButtonCharacter: Phaser.GameObjects.Image | null = null;
  public hudButtonInventory: Phaser.GameObjects.Image | null = null;
  public hudButtonSpirit: Phaser.GameObjects.Image | null = null;
  public hudButtonSpells: Phaser.GameObjects.Image | null = null;
  public hudButtonQuests: Phaser.GameObjects.Image | null = null;
  public hudButtons: Phaser.GameObjects.Image[] = [];

  constructor(private scene: GameScene) {}

  createHUD(): void {
    const HUD_DEPTH = 1000; // Por encima de todo el contenido del juego

    // Cargar escala guardada
    const saved = this.scene.persistence.loadClientOptions();
    if (saved?.hudScale !== undefined) {
      this.hudScale = this.scene.getClosestHudScale(saved.hudScale);
    }

    const { width } = this.scene.scale;
    const cx = width / 2;

    // --- Piezas estáticas (origin/ancla fijas; escala+posición en updateHUDPositions) ---

    // 1. Barra de XP (frame + fill) al pie de la pantalla.
    const xpFrame = this.scene.add.image(cx, 0, 'main_xp_empty_frame')
      .setOrigin(0.5, 1).setScrollFactor(0).setDepth(HUD_DEPTH);
    this.hudXpFrame = xpFrame;
    const xpFill = this.scene.add.image(cx, 0, 'main_xp_fill')
      .setOrigin(0.5, 1).setScrollFactor(0).setDepth(HUD_DEPTH + 1);
    this.hudExpBar = xpFill;
    this.hudExpBarOriginalWidth = (this.scene.textures.get('main_xp_fill').getSourceImage() as HTMLImageElement).width;
    xpFill.setCrop(0, 0, 0, xpFill.height); // arranca vacío

    // Texto de XP centrado en la barra: % (2 decimales, coma) por defecto; click alterna a
    // actual/siguiente nivel y vuelve.
    this.hudPctText = this.scene.addText(cx, 0, '0,00%', {
      fontSize: '9px', color: '#eaffea', fontStyle: 'bold', stroke: '#000000', strokeThickness: 2, resolution: 3,
    }).setOrigin(0.5, 0.5).setScrollFactor(0).setDepth(HUD_DEPTH + 12).setInteractive({ useHandCursor: true });
    this.hudPctText.on('pointerup', (p: Phaser.Input.Pointer) => {
      this.hudPctShowRaw = !this.hudPctShowRaw;
      this.updateHUDStats();
      p.event.stopPropagation();
    });

    // 2. main_frame (barra inferior con recesos de slots).
    this.hudMainFrame = this.scene.add.image(cx, 0, 'main_frame')
      .setOrigin(0.5, 1).setScrollFactor(0).setDepth(HUD_DEPTH + 2);

    this.hudElements = [xpFrame, xpFill, this.hudMainFrame];

    // 3. Globos laterales (vida izq = rojo, mana der = azul). Recolor tomando el color
    //    de las barras (rojo de main_fill_red / azul de main_fill_blue).
    const redColor = this.averageOpaqueColor('main_fill_red');
    const blueColor = this.averageOpaqueColor('main_fill_blue');
    this.ensureRecoloredGlobe('main_globe_fill_green', 'main_globe_fill_health', redColor);
    this.ensureRecoloredGlobe('main_globe_fill_orange', 'main_globe_fill_mana', blueColor);

    // Centro REAL del círculo del socket en px nativos → fracción de origin del empty.
    // (medido: el círculo no está en el centro del canvas; usar el centroide real evita
    //  que el fill/overlay queden descentrados respecto al socket.)
    const SOCKET_L = { ox: 148 / 361, oy: 147 / 343 };
    const SOCKET_R = { ox: 212 / 361, oy: 147 / 343 };
    const buildGlobe = (side: 'left' | 'right', fillKey: string, depth: number):
      { empty: Phaser.GameObjects.Image; fill: Phaser.GameObjects.Image; fx: Phaser.GameObjects.Image; bl: Phaser.GameObjects.Image; layers: Phaser.GameObjects.Image[] } => {
      const emptyKey = side === 'left' ? 'main_globe_empty_left' : 'main_globe_empty_right';
      const sk = side === 'left' ? SOCKET_L : SOCKET_R;
      // Socket: origin = centro del círculo → su círculo cae en el ancla del globo.
      const empty = this.scene.add.image(0, 0, emptyKey).setOrigin(sk.ox, sk.oy).setScrollFactor(0).setDepth(depth);
      // Fill (círculo), crop desde arriba; centrado en el socket.
      const fill = this.scene.add.image(0, 0, fillKey).setOrigin(0.5, 0.5).setScrollFactor(0).setDepth(depth + 1);
      const fx = this.scene.add.image(0, 0, 'main_globe_overlay_effects')
        .setOrigin(0.5, 0.5).setScrollFactor(0).setDepth(depth + 2).setAlpha(0.4);
      const bl = this.scene.add.image(0, 0, 'main_globe_overlay_bl')
        .setOrigin(0.5, 0.5).setScrollFactor(0).setDepth(depth + 2).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0.45);
      const border = this.scene.add.image(0, 0, 'main_globe_border_overlay')
        .setOrigin(0.5, 0.5).setScrollFactor(0).setDepth(depth + 3);
      return { empty, fill, fx, bl, layers: [empty, fill, fx, bl, border] };
    };

    const hpGlobe = buildGlobe('left', 'main_globe_fill_health', HUD_DEPTH + 6);
    this.hudHpGlobeEmpty = hpGlobe.empty;
    this.hudHpGlobeFill = hpGlobe.fill;
    this.hudHpGlobeFx = hpGlobe.fx;
    this.hudHpGlobeBl = hpGlobe.bl;
    this.hudHpGlobeFillH = (this.scene.textures.get('main_globe_fill_health').getSourceImage() as HTMLImageElement).height;
    this.hudHpGlobeLayers = hpGlobe.layers;

    const manaGlobe = buildGlobe('right', 'main_globe_fill_mana', HUD_DEPTH + 6);
    this.hudManaGlobeBl = manaGlobe.bl;
    this.hudManaGlobeFx = manaGlobe.fx;
    this.hudManaGlobeEmpty = manaGlobe.empty;
    this.hudManaGlobeFill = manaGlobe.fill;
    this.hudManaGlobeFillH = (this.scene.textures.get('main_globe_fill_mana').getSourceImage() as HTMLImageElement).height;
    this.hudManaGlobeLayers = manaGlobe.layers;

    // 3b. Banda de mana RESERVADA: recolor más CLARO, ocupa la franja superior del globo
    //     (lo reservado por auras, que NO es mana usable). Se posiciona con el globo.
    this.ensureRecoloredGlobe('main_globe_fill_orange', 'main_globe_fill_mana_reserve', { r: 150, g: 200, b: 255 });
    const reserveImg = this.scene.add.image(0, 0, 'main_globe_fill_mana_reserve')
      .setOrigin(0.5, 0.5).setScrollFactor(0).setDepth(HUD_DEPTH + 6 + 1.5).setAlpha(0.85).setVisible(false);
    this.hudManaReserveGlobe = reserveImg;
    this.hudManaReserveGlobeH = (this.scene.textures.get('main_globe_fill_mana_reserve').getSourceImage() as HTMLImageElement).height;
    this.hudManaGlobeLayers.push(reserveImg); // escala/posiciona con el globo de mana

    // Vida a los globos: PARTÍCULAS tipo líquido/burbujas moviéndose adentro, recortadas por una
    // máscara geométrica del SEGMENTO de líquido actual (no se dibujan sobre la reserva ni el vacío).
    this.ensureBubbleTexture();
    this.hudHpLiquidMask = this.scene.add.graphics().setScrollFactor(0).setVisible(false);
    this.hudManaLiquidMask = this.scene.add.graphics().setScrollFactor(0).setVisible(false);
    const hpB = this.createOrbBubbles(this.hudHpLiquidMask, 0xffcab8, 700, HUD_DEPTH + 6);
    this.hudHpBubbles = hpB.emitter; this.hudHpBubbleZone = hpB.zone;
    const manaB = this.createOrbBubbles(this.hudManaLiquidMask, 0xcfe2ff, 820, HUD_DEPTH + 6);
    this.hudManaBubbles = manaB.emitter; this.hudManaBubbleZone = manaB.zone;
    // Los overlays del globo (effects + bl) también se recortan al líquido actual: si falta
    // stat (el fill baja), no deben dibujarse sobre el vacío. Misma máscara geométrica que el humo.
    this.hudHpGlobeFx?.setMask(this.hudHpLiquidMask.createGeometryMask());
    this.hudHpGlobeBl?.setMask(this.hudHpLiquidMask.createGeometryMask());
    this.hudManaGlobeFx?.setMask(this.hudManaLiquidMask.createGeometryMask());
    this.hudManaGlobeBl?.setMask(this.hudManaLiquidMask.createGeometryMask());

    // 4. Labels n/n SOBRE cada globo.
    this.hudHpLabel = this.scene.addText(0, 0, '0/0', {
      fontSize: '20px', color: '#ffffff', fontStyle: 'bold', stroke: '#000000', strokeThickness: 2, resolution: 3,
    }).setOrigin(0.5, 0.5).setScrollFactor(0).setDepth(HUD_DEPTH + 11).setLetterSpacing(1);

    this.hudManaLabel = this.scene.addText(0, 0, '0/0', {
      fontSize: '20px', color: '#ffffff', fontStyle: 'bold', stroke: '#000000', strokeThickness: 2, resolution: 3,
    }).setOrigin(0.5, 0.5).setScrollFactor(0).setDepth(HUD_DEPTH + 11).setLetterSpacing(1);

    // Label de mana reservada (debajo del n/n de mana), en celeste claro.
    this.hudManaReserveLabel = this.scene.addText(0, 0, '', {
      fontSize: '14px', color: '#a9d4ff', fontStyle: 'bold', stroke: '#000000', strokeThickness: 2, resolution: 3,
    }).setOrigin(0.5, 0.5).setScrollFactor(0).setDepth(HUD_DEPTH + 11).setVisible(false);

    // 7. Keycaps de las posiciones NO funcionales (-,=,R,T): caja = asset
    //    main_shortcut_frame para TODOS (R/T iguales al resto, no el frame small).
    //    Los recesos 7,8,9,0 ahora son slots FUNCIONALES (SpellQuickBar 10 slots),
    //    con su propio keycap; acá quedan solo los reservados a la derecha. R/T son
    //    los recesos CHICOS del extremo derecho, reservados para CONSUMIBLES a futuro
    //    (no llevan hechizo configurable). El posicionamiento vive en updateHUDPositions.
    this.hudExtraKeycaps = [];
    this.hudExtraKeyLabels = [];
    for (const key of ['-', '=', 'R', 'T']) {
      const keycap = this.scene.add.image(0, 0, 'main_shortcut_frame')
        .setOrigin(0.5, 0.5).setScrollFactor(0).setDepth(HUD_DEPTH + 5);
      const lbl = this.scene.addText(0, 0, key, {
        fontSize: '12px', color: '#ece6d6', fontStyle: 'bold', resolution: 3,
      }).setOrigin(0.5, 0.5).setScrollFactor(0).setDepth(HUD_DEPTH + 6);
      this.hudExtraKeycaps.push(keycap);
      this.hudExtraKeyLabels.push(lbl);
    }

    // 8. Botones funcionales: por ahora flotando en una columna a la derecha (fuera del HUD).
    const HUD_BUTTONS_DEPTH = HUD_DEPTH + 10;
    this.hudButtonCharacter = this.scene.add.image(0, 0, 'hud_button_character')
      .setOrigin(0.5, 0.5).setScrollFactor(0).setDepth(HUD_BUTTONS_DEPTH).setInteractive({ useHandCursor: true });
    this.hudButtonInventory = this.scene.add.image(0, 0, 'hud_button_inventory')
      .setOrigin(0.5, 0.5).setScrollFactor(0).setDepth(HUD_BUTTONS_DEPTH).setInteractive({ useHandCursor: true });
    this.hudButtonSpells = this.scene.add.image(0, 0, 'hud_button_spells')
      .setOrigin(0.5, 0.5).setScrollFactor(0).setDepth(HUD_BUTTONS_DEPTH).setInteractive({ useHandCursor: true });
    this.hudButtonSpirit = this.scene.add.image(0, 0, 'hud_button_spirit')
      .setOrigin(0.5, 0.5).setScrollFactor(0).setDepth(HUD_BUTTONS_DEPTH).setInteractive({ useHandCursor: true });
    // Botón de quests: no hay asset, se genera por canvas (parchment + ícono de lista).
    this.createQuestButtonTextures();
    this.hudButtonQuests = this.scene.add.image(0, 0, 'hud_button_quests')
      .setOrigin(0.5, 0.5).setScrollFactor(0).setDepth(HUD_BUTTONS_DEPTH).setInteractive({ useHandCursor: true });

    this.hudButtons = [
      this.hudButtonCharacter,
      this.hudButtonInventory,
      this.hudButtonSpells,
      this.hudButtonSpirit,
      this.hudButtonQuests,
    ];

    this.setupHUDButtons();

    // Botón de pantalla completa: glyph en la fila 1 superior-derecha, a la izquierda de la
    // tuerca ⚙ (donde antes estaba el sol ☀️). Posición final en updateHUDPositions. pointerup
    // corre dentro del gesto del usuario, requisito de la Fullscreen API.
    this.scene.fullscreenButton = this.scene.addText(0, 0, '⛶', {
      fontSize: '22px', color: '#ece6d6', stroke: '#000000', strokeThickness: 3, resolution: 3,
    })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(HUD_BUTTONS_DEPTH)
      .setInteractive({ useHandCursor: true });
    this.scene.fullscreenButton.on('pointerover', () => this.scene.fullscreenButton?.setColor('#ffffff'));
    this.scene.fullscreenButton.on('pointerout', () => this.scene.fullscreenButton?.setColor('#ece6d6'));
    this.scene.fullscreenButton.on('pointerup', () => this.scene.fullscreen.toggleFullscreen());
    this.scene.fullscreen.refreshFullscreenButton();

    // El juego usa pixelArt (nearest-neighbor) global; al REDUCIR el arte hi-res del HUD
    // (main_frame 1937px → ~760) las líneas de 1px se pierden. Filtro LINEAR en las texturas
    // del HUD → downscale suave que conserva las líneas, manteniendo el ratio/posiciones.
    for (const k of [
      'main_frame', 'main_shortcut_frame', 'main_shortcut_frame_small',
      'main_xp_empty_frame', 'main_xp_fill',
      'main_globe_empty_left', 'main_globe_empty_right', 'main_globe_overlay_effects',
      'main_globe_overlay_bl', 'main_globe_border_overlay',
      'main_globe_fill_health', 'main_globe_fill_mana', 'main_globe_fill_mana_reserve',
      'spell_frame_icon_OVERLAY', 'spell_frame_hover_effect', 'spell_frame_push_effect', 'spell_frame_cooldown_effect',
    ]) {
      if (this.scene.textures.exists(k)) this.scene.textures.get(k).setFilter(Phaser.Textures.FilterMode.LINEAR);
    }

    this.updateHUDPositions();
  }

  /**
   * Pre-rinde una textura a `scale` (downscale de ALTA calidad por canvas) y la registra en
   * `dstKey`, para dibujarla luego a escala 1. Evita el downscale GPU no-entero que deja las
   * líneas 1px con brillo dispar. Re-bakea sólo si cambió el tamaño objetivo.
   */
  private bakeScaledTexture(srcKey: string, dstKey: string, scale: number): void {
    const src = this.scene.textures.get(srcKey).getSourceImage() as HTMLImageElement;
    const w = Math.max(1, Math.round(src.width * scale));
    const h = Math.max(1, Math.round(src.height * scale));
    if (this.scene.textures.exists(dstKey)) {
      const ex = this.scene.textures.get(dstKey).getSourceImage() as HTMLCanvasElement;
      if (ex.width === w && ex.height === h) return; // ya bakeado a este tamaño
      this.scene.textures.remove(dstKey);
    }
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(src, 0, 0, w, h);
    this.scene.textures.addCanvas(dstKey, canvas);
  }

  /** Textura de burbuja: blob suave (gradiente radial) con un realce central tenue. */
  private ensureBubbleTexture(): void {
    const key = 'hud_bubble';
    if (this.scene.textures.exists(key)) return;
    const S = 48;
    const canvas = document.createElement('canvas');
    canvas.width = S; canvas.height = S;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const g = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
    g.addColorStop(0, 'rgba(255,255,255,0.9)');
    g.addColorStop(0.45, 'rgba(255,255,255,0.5)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, S, S);
    this.scene.textures.addCanvas(key, canvas);
  }

  /**
   * Partículas tipo líquido/burbujas dentro de un orbe: nacen repartidas en el líquido, suben
   * lento con deriva aleatoria, de tamaños variados, y se aparecen/disuelven. Recortadas por la
   * máscara geométrica del líquido actual (segmento del círculo bajo la superficie → no se dibujan
   * sobre la reserva ni el vacío). Devuelve emitter + zona (se reposicionan en updateHUDPositions).
   */
  private createOrbBubbles(
    liquidMask: Phaser.GameObjects.Graphics | null,
    tint: number,
    frequency: number,
    depth: number
  ): { emitter: Phaser.GameObjects.Particles.ParticleEmitter; zone: Phaser.Geom.Circle } {
    const zone = new Phaser.Geom.Circle(0, 0, 40);
    const emitter = this.scene.add.particles(0, 0, 'hud_bubble', {
      lifespan: { min: 3800, max: 7500 },     // largo → movimiento lento
      speedX: { min: -6, max: 6 },            // deriva lateral aleatoria
      speedY: { min: -11, max: -2 },          // suben lento
      scale: { min: 0.55, max: 1.1 },         // POCAS y GRANDES (tipo humo)
      alpha: { values: [0, 0.22, 0] },        // aparecen y se disuelven (sutil, poca opacidad)
      frequency,
      quantity: 1,
      tint,
      emitZone: { type: 'random', source: zone, quantity: 1 },
    });
    emitter.setScrollFactor(0).setDepth(depth + 7.5);
    // Máscara geométrica = segmento de líquido actual → las partículas sólo se ven sobre el
    // líquido visible (redibujada en redrawLiquidMasks según el nivel del orbe).
    if (liquidMask) emitter.setMask(liquidMask.createGeometryMask());
    return { emitter, zone };
  }

  /**
   * Dibuja en `g` el segmento de líquido de un orbe: el trozo del círculo (centro c, radio R)
   * que queda POR DEBAJO de la superficie dada por `frac` (0 = vacío, 1 = lleno). Usado como
   * stencil de máscara geométrica para el humo de las orbes.
   */
  private drawLiquidSegment(
    g: Phaser.GameObjects.Graphics | null,
    c: { x: number; y: number },
    R: number,
    frac: number
  ): void {
    if (!g) return;
    g.clear();
    const f = Phaser.Math.Clamp(frac, 0, 1);
    if (f <= 0 || R <= 0) return;
    g.fillStyle(0xffffff, 1);
    if (f >= 1) {
      g.fillCircle(c.x, c.y, R);
      return;
    }
    // Superficie a y = cy + dy (screen y hacia abajo). f=1 → dy=-R (arriba); f=0 → dy=+R (abajo).
    const dy = R * (1 - 2 * f);
    const hw = Math.sqrt(Math.max(0, R * R - dy * dy));
    const a = Math.atan2(dy, hw); // ángulo de la intersección derecha
    // Arco horario de a → π-a (pasa por π/2 = fondo) + cierre por la cuerda → segmento inferior.
    g.beginPath();
    g.arc(c.x, c.y, R, a, Math.PI - a, false);
    g.closePath();
    g.fillPath();
  }

  /** Redibuja las dos máscaras de líquido con la geometría/fracción actuales de cada orbe. */
  private redrawLiquidMasks(): void {
    this.drawLiquidSegment(this.hudHpLiquidMask, this.hudHpOrbCenter, this.hudOrbRadius, this.hudHpFillFrac);
    this.drawLiquidSegment(this.hudManaLiquidMask, this.hudManaOrbCenter, this.hudOrbRadius, this.hudManaFillFrac);
  }

  /** Color promedio (RGB) de los pixeles opacos de una textura. */
  private averageOpaqueColor(srcKey: string): { r: number; g: number; b: number } {
    const src = this.scene.textures.get(srcKey).getSourceImage() as HTMLImageElement;
    const w = src.width, h = src.height;
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return { r: 200, g: 60, b: 60 };
    ctx.drawImage(src, 0, 0);
    const data = ctx.getImageData(0, 0, w, h).data;
    let r = 0, g = 0, b = 0, n = 0;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 40) continue;
      r += data[i]; g += data[i + 1]; b += data[i + 2]; n++;
    }
    if (n === 0) return { r: 200, g: 60, b: 60 };
    return { r: r / n, g: g / n, b: b / n };
  }

  /**
   * Recolorea una esfera (globe fill) al color dado, conservando su sombreado:
   * para cada pixel, salida = color · (luminancia/ref), con el alpha original.
   */
  private ensureRecoloredGlobe(srcKey: string, dstKey: string, color: { r: number; g: number; b: number }): void {
    if (this.scene.textures.exists(dstKey)) return;
    const src = this.scene.textures.get(srcKey).getSourceImage() as HTMLImageElement;
    const w = src.width, h = src.height;
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    ctx.drawImage(src, 0, 0);
    const img = ctx.getImageData(0, 0, w, h);
    const d = img.data;
    const REF = 150; // luminancia neutra: arriba aclara, abajo oscurece
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] === 0) continue;
      const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
      const f = lum / REF;
      d[i] = Math.min(255, color.r * f);
      d[i + 1] = Math.min(255, color.g * f);
      d[i + 2] = Math.min(255, color.b * f);
    }
    ctx.putImageData(img, 0, 0);
    this.scene.textures.addCanvas(dstKey, canvas);
  }

  /**
   * Layout completo del HUD (escala + posición de todas las piezas del skin ui/main).
   * Reemplaza tanto al viejo updateHUDPositions como a applyHUDScale.
   */
  updateHUDPositions(): void {
    const { width, height } = this.scene.scale;
    const cx = width / 2;

    // Geometría nativa de los assets (px del arte).
    const FRAME_W = 1937, FRAME_H = 233, FRAME_CX = FRAME_W / 2;

    // Escala de display: con HUD_BASE_FRAC el main_frame ocupa ~5/8 del ancho del
    // canvas a hudScale default (2). hudScale (1..4) actúa como zoom relativo.
    const HUD_BASE_FRAC = 0.625;
    const s = (width / FRAME_W) * (this.hudScale / 2) * HUD_BASE_FRAC;
    // Globos a la MISMA escala que el frame: el bracket del globo calza con el end-cap
    // del frame a 1:1 (el globo igual queda más alto porque su arte es 343 vs 233px).
    const gs = s;

    // Anclas verticales (origin 0.5,1). El frame se eleva para que asome la barra XP.
    // El +1 final ajusta el HUD entero respecto al borde inferior: lo bajamos 6px
    // y después lo subimos 5px (así se ve mejor lo que pasa debajo en combate).
    const frameBottomY = height - Math.round(28 * s) + 1;
    const frameTopY = frameBottomY - FRAME_H * s;

    // Helper native→screen del main_frame.
    const fr = (nx: number, ny: number): { x: number; y: number } => ({
      x: cx + (nx - FRAME_CX) * s,
      y: frameBottomY - (FRAME_H - ny) * s,
    });

    // Piezas estáticas. El main_frame tiene ~36px nativos transparentes abajo; pegamos la
    // barra de XP justo debajo de ese borde visible (sin gap), arriba del piso de pantalla.
    const FRAME_BOT_PAD = 36;
    const xpY = frameBottomY - FRAME_BOT_PAD * s + 54 * s - 4; // borde visible del frame, 4px arriba
    // main_frame: pre-rendido a escala s (canvas HQ) y dibujado a escala 1 → líneas parejas.
    this.bakeScaledTexture('main_frame', 'main_frame_hq', s);
    this.hudMainFrame?.setTexture('main_frame_hq').setScale(1).setPosition(cx, frameBottomY);
    this.hudXpFrame?.setScale(s).setPosition(cx, xpY);
    this.hudExpBar?.setScale(s).setPosition(cx, xpY - 3); // solo el fill, 3px arriba del frame
    // Texto de XP centrado vertical en la barra (54px nativos de alto), 1px más abajo.
    this.hudPctText?.setPosition(cx, xpY - 54 * s * 0.5 + 1);

    // --- Globos laterales: socket center pegado a cada extremo del main_frame ---
    // El centro del globo cae un poco por fuera del extremo del frame; vertical ~ medio
    // del frame, asomando hacia arriba. Tuneable contra el arte.
    const frameLeftX = cx - FRAME_CX * s;
    const frameRightX = cx + FRAME_CX * s;
    // El socket queda POR FUERA del extremo del frame; el bracket del globo (que apunta
    // hacia adentro) se funde con el end-cap del frame. Tuneado en vivo.
    const GLOBE_DX = 70;       // px nativos hacia afuera del extremo del frame
    const globeY = frameTopY + FRAME_H * s * 0.30 - 9.5; // posición del globo (subido 4.5px)
    const hpC = { x: frameLeftX - GLOBE_DX * s + 2.5, y: globeY };  // vida 0.5px más a la derecha
    const manaC = { x: frameRightX + GLOBE_DX * s - 2.5, y: globeY }; // mana 0.5px más a la izquierda
    for (const img of this.hudHpGlobeLayers) img.setScale(gs).setPosition(hpC.x, hpC.y);
    for (const img of this.hudManaGlobeLayers) img.setScale(gs).setPosition(manaC.x, manaC.y);
    // overlay_bl: es más chico que el globo → lo corro arriba-izquierda (tuneable en vivo).
    const BL_DX = 10, BL_DY = 10;
    this.hudHpGlobeBl?.setPosition(hpC.x - BL_DX, hpC.y - BL_DY);
    this.hudManaGlobeBl?.setPosition(manaC.x - BL_DX, manaC.y - BL_DY);

    // Burbujas dentro de cada orbe: emitter en el centro, zona = círculo del fill. La máscara
    // geométrica del líquido (redibujada por nivel) las recorta al líquido visible.
    const orbR = 122 * gs; // radio del fill del globo (≈130 nativo)
    const placeBubbles = (
      em: Phaser.GameObjects.Particles.ParticleEmitter | null,
      zn: Phaser.Geom.Circle | null,
      c: { x: number; y: number }
    ): void => {
      if (zn) zn.setTo(0, 0, orbR);
      em?.setPosition(c.x, c.y);
    };
    placeBubbles(this.hudHpBubbles, this.hudHpBubbleZone, hpC);
    placeBubbles(this.hudManaBubbles, this.hudManaBubbleZone, manaC);
    // Guardar geometría del orbe y redibujar las máscaras de líquido (centro/radio pueden
    // cambiar por resize o cambio de hudScale).
    this.hudHpOrbCenter = { x: hpC.x, y: hpC.y };
    this.hudManaOrbCenter = { x: manaC.x, y: manaC.y };
    this.hudOrbRadius = orbR;
    this.redrawLiquidMasks();

    // Labels n/n centrados SOBRE cada globo (texto legible); reserva debajo del de mana.
    const globeFont = Math.max(24, Math.round(72 * gs) + 1); // 2× (pedido de legibilidad 2026-07-11)
    this.hudHpLabel?.setPosition(hpC.x, hpC.y).setFontSize(globeFont);
    this.hudManaLabel?.setPosition(manaC.x, manaC.y).setFontSize(globeFont);
    this.hudManaReserveLabel?.setPosition(manaC.x, manaC.y + globeFont * 0.95).setFontSize(Math.max(9, Math.round(globeFont * 0.7)));

    // Slots grandes (1-9,0) sobre los 10 primeros recesos del main_frame.
    const SLOT_ROW_Y = 115;
    const BIG_BASE = 186.5, BIG_PITCH = 126.7;
    // Nudge fino del PRIMER slot en píxeles de PANTALLA. Como el resto se deriva
    // del pitch fijo (base + i·pitch), todos se desplazan lo mismo y conservan la
    // distancia entre slots. Negativo = arriba / izquierda.
    const SLOT_NUDGE_X = -1; // 1px a la izquierda (corrido 1 a la derecha)
    const SLOT_NUDGE_Y = -3; // 3px arriba
    // Crecimiento del slot anclado al TOP-LEFT: +2px de ancho y +2px de alto. Como
    // el slot se posiciona por su CENTRO, corremos el centro +SLOT_GROW/2 para que la
    // esquina superior-izquierda quede fija y el slot crezca hacia abajo-derecha.
    const SLOT_GROW = 2; // +2px ancho y +2px alto, anclado al TOP-LEFT
    const SLOT_GROW_W = 1; // +1px ancho EXTRA, anclado al borde IZQUIERDO (crece a la derecha)
    const bigSlotH = Math.round(110 * s) + SLOT_GROW; // alto = interior del receso (~111) + 2
    const bigSlotW = bigSlotH + SLOT_GROW_W; // ancho = alto + 1 (slot ligeramente no-cuadrado)
    const bigPositions: Array<{ x: number; y: number }> = [];
    for (let i = 0; i < 10; i++) {
      const p = fr(BIG_BASE + i * BIG_PITCH, SLOT_ROW_Y);
      bigPositions.push({
        // top-left fijo para el +2; el +1 de ancho corre el centro otro +0.5 (borde izq fijo).
        x: p.x + SLOT_NUDGE_X + SLOT_GROW / 2 + SLOT_GROW_W / 2,
        y: p.y + SLOT_NUDGE_Y + SLOT_GROW / 2,
      });
    }
    this.scene.spellQuickBar?.setLayout(bigPositions, bigSlotH, bigSlotW);

    // Buff/debuff bar: arranca en el borde IZQUIERDO (top-left) del slot 1, arriba de la fila.
    const slot1 = bigPositions[0];
    if (slot1) this.scene.buffBar?.setAnchor(slot1.x - bigSlotW / 2, slot1.y - bigSlotH / 2 - 25);

    // Keycaps de posiciones NO funcionales (orden del array this.hudExtraKeycaps:
    // [ '-', '=', 'R', 'T' ]). TODOS son keycaps chicos al PIE de su receso, en la
    // MISMA fila que los números 1-0 (kcY): así la letra queda abajo y el cuadrado
    // del receso queda LIBRE para el ícono (los recesos chicos R/T son los futuros
    // slots de CONSUMIBLES; no llevan hechizo configurable). Sólo cambia el X:
    //  • '-' y '=' sobre los recesos GRANDES 11 y 12 (mismo pitch que 1-0).
    //  • 'R' y 'T' sobre los recesos CHICOS del extremo derecho, con sus centros X
    //    medidos del arte de main_frame (nativo, frame 1937px): x≈1694 y 1774.
    const slotRowY = fr(0, SLOT_ROW_Y).y;
    const slotSize = Math.round(110 * s); // alto del receso grande
    const kc = Math.round(slotSize * 0.46);
    // El número llena la cajita del keycap (antes ocupaba ~31% y no se leía).
    const kcFont = Math.max(14, Math.round(kc * 0.6) + 1);
    const kcY = slotRowY + slotSize / 2; // al pie del receso (fila de keycaps, como 1-0)
    const EXTRA_CENTERS = [
      BIG_BASE + 10 * BIG_PITCH, // -
      BIG_BASE + 11 * BIG_PITCH, // =
      1694, // R (receso chico)
      1774, // T (receso chico)
    ];
    for (let i = 0; i < this.hudExtraKeycaps.length; i++) {
      const x = cx + (EXTRA_CENTERS[i] - FRAME_CX) * s;
      this.hudExtraKeycaps[i]?.setPosition(x, kcY).setDisplaySize(kc, kc);
      this.hudExtraKeyLabels[i]?.setPosition(x, kcY).setFontSize(kcFont);
    }

    // Botones funcionales flotando en columna a la derecha (provisorio).
    const BTN_W = 22, BTN_H = 38, BTN_GAP = 8;
    const colX = width - 28;
    const totalH = this.hudButtons.length * BTN_H + (this.hudButtons.length - 1) * BTN_GAP;
    let by = height / 2 - totalH / 2 + BTN_H / 2;
    for (const b of this.hudButtons) {
      if (!b) continue;
      b.setDisplaySize(BTN_W, BTN_H).setPosition(colX, by);
      by += BTN_H + BTN_GAP;
    }

    // Botón de pantalla completa: fila 1 superior-derecha, a la izquierda de la tuerca ⚙
    // (misma posición que tenía el sol: width - 20 - buttonGap, con buttonGap = 40). Origin (1,0).
    this.scene.fullscreenButton?.setPosition(width - 20 - 40, 10);
  }

  /**
   * Actualiza los valores de HP/Mana/XP en el HUD (crop horizontal).
   */
  updateHUDStats(): void {
    if (!this.scene.room || !this.scene.localSessionId) return;
    const playerState = this.scene.room.state.players.get(this.scene.localSessionId);
    if (!playerState) return;

    const hp = playerState.minHp ?? 0;
    const maxHp = playerState.maxHp ?? 100;
    const mana = playerState.minMana ?? 0;
    const maxMana = playerState.maxMana ?? 50;
    const baseMax = (playerState as any).baseMaxMana ?? maxMana;
    const displayMax = baseMax > 0 ? baseMax : maxMana;

    // Labels n/n sobre los globos.
    if (this.hudHpLabel) {
      this.hudHpLabel.setText(`${Math.floor(hp)}/${Math.floor(maxHp)}`);
    }
    if (this.hudManaLabel) {
      this.hudManaLabel.setText(`${Math.floor(mana)}/${Math.floor(displayMax)}`);
    }
    // Mana reservada: label debajo del n/n.
    if (this.hudManaReserveLabel) {
      if (this.scene.manaReservedPercent > 0) {
        const reserved = Math.round(baseMax * this.scene.manaReservedPercent);
        this.hudManaReserveLabel.setText(`-${reserved} reservado`);
        this.hudManaReserveLabel.setVisible(true);
      } else {
        this.hudManaReserveLabel.setVisible(false);
      }
    }

    // Barra de XP (crop izquierda→derecha).
    {
      const exp = (playerState as any).experience ?? 0;
      const expNext = (playerState as any).expForNextLevel ?? 1;
      const frac = expNext > 0 ? Phaser.Math.Clamp(exp / expNext, 0, 1) : 0;
      if (this.hudPctText) {
        const fixed = (frac * 100).toFixed(2); // "45.00" | "45.23"
        const pctStr = fixed.endsWith('.00') ? fixed.slice(0, -3) : fixed.replace('.', ','); // decimales sólo si hacen falta
        this.hudPctText.setText(this.hudPctShowRaw ? `${Math.floor(exp)}/${Math.floor(expNext)}` : `${pctStr}%`);
      }
      if (this.hudExpBar) {
        const cropW = Math.floor(this.hudExpBarOriginalWidth * frac);
        this.hudExpBar.setCrop(0, 0, cropW, this.hudExpBar.height);
      }
    }

    // Globos: crop vertical DESDE ARRIBA (la esfera se vacía de arriba hacia abajo).
    this.hudHpFillFrac = maxHp > 0 ? hp / maxHp : 0;
    this.hudManaFillFrac = baseMax > 0 ? mana / baseMax : 0;
    this.cropGlobeFill(this.hudHpGlobeFill, this.hudHpGlobeFillH, this.hudHpFillFrac);
    this.cropGlobeFill(this.hudManaGlobeFill, this.hudManaGlobeFillH, this.hudManaFillFrac);
    // Las máscaras del humo siguen el nivel actual del líquido (no sobre reserva ni vacío).
    this.redrawLiquidMasks();

    // Banda de mana reservada: franja SUPERIOR del globo (color claro), alto = % reservado.
    if (this.hudManaReserveGlobe && this.hudManaReserveGlobeH > 0) {
      const r = Phaser.Math.Clamp(this.scene.manaReservedPercent, 0, 1);
      if (r > 0) {
        const band = Math.round(this.hudManaReserveGlobeH * r);
        this.hudManaReserveGlobe.setCrop(0, 0, this.hudManaReserveGlobe.width, band);
        this.hudManaReserveGlobe.setVisible(true);
      } else {
        this.hudManaReserveGlobe.setVisible(false);
      }
    }
  }

  /** Recorta un fill de globo (círculo) desde arriba según la fracción [0..1]. */
  private cropGlobeFill(fill: Phaser.GameObjects.Image | null, nativeH: number, frac: number): void {
    if (!fill || nativeH <= 0) return;
    const f = Phaser.Math.Clamp(frac, 0, 1);
    const visibleH = Math.round(nativeH * f);
    const cropY = nativeH - visibleH; // recorta desde arriba → muestra el fondo
    fill.setCrop(0, cropY, fill.width, visibleH);
    fill.setVisible(visibleH > 0);
  }

  private applyHUDScale(): void {
    // Toda la escala/posición vive en updateHUDPositions (depende de hudScale).
    this.updateHUDPositions();
  }

  setHUDScale(scale: number): void {
    this.hudScale = scale;

    this.applyHUDScale();

    this.scene.persistence.saveClientOptions();
  }

  /**
   * Configura los eventos de hover y click para los botones del HUD
   */
  /**
   * Genera por canvas las texturas del botón de quests (no hay asset propio):
   * `hud_button_quests` + `_hover`. Dibuja un fondo oscuro con borde dorado y un
   * ícono de "lista/pergamino" (rect claro con 3 líneas) para leerse como log.
   */
  private createQuestButtonTextures(): void {
    if (this.scene.textures.exists('hud_button_quests')) return;
    const W = 44;
    const H = 76;
    const draw = (key: string, bg: string, border: string): void => {
      const canvas = document.createElement('canvas');
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      // Fondo + borde.
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = border;
      ctx.lineWidth = 3;
      ctx.strokeRect(1.5, 1.5, W - 3, H - 3);
      // Pergamino central.
      const px = 10;
      const py = 16;
      const pw = W - 20;
      const ph = H - 32;
      ctx.fillStyle = '#d8c79a';
      ctx.fillRect(px, py, pw, ph);
      ctx.strokeStyle = '#8a7a4a';
      ctx.lineWidth = 1;
      ctx.strokeRect(px + 0.5, py + 0.5, pw - 1, ph - 1);
      // Líneas de "texto" del log.
      ctx.fillStyle = '#5a4d2e';
      for (let i = 0; i < 3; i++) {
        const ly = py + 8 + i * 9;
        ctx.fillRect(px + 5, ly, pw - 10, 2);
      }
      this.scene.textures.addCanvas(key, canvas);
    };
    draw('hud_button_quests', '#2a2418', '#8a7a4a');
    draw('hud_button_quests_hover', '#3a3220', '#c8ad5e');
  }

  setupHUDButtons(): void {
    // Botón de información del personaje
    if (this.hudButtonCharacter) {
      this.hudButtonCharacter.on('pointerover', () => {
        this.hudButtonCharacter?.setTexture('hud_button_character_hover');
      });
      this.hudButtonCharacter.on('pointerout', () => {
        this.hudButtonCharacter?.setTexture('hud_button_character');
      });
      this.hudButtonCharacter.on('pointerup', (pointer: Phaser.Input.Pointer) => {
        this.scene.toggleCharacterInfoPanel();
        pointer.event.stopPropagation();
      });
    }

    // Botón de inventario
    if (this.hudButtonInventory) {
      this.hudButtonInventory.on('pointerover', () => {
        this.hudButtonInventory?.setTexture('hud_button_inventory_hover');
      });
      this.hudButtonInventory.on('pointerout', () => {
        this.hudButtonInventory?.setTexture('hud_button_inventory');
      });
      this.hudButtonInventory.on('pointerup', (pointer: Phaser.Input.Pointer) => {
        this.scene.toggleInventoryPanel();
        pointer.event.stopPropagation();
      });
    }

    // Botón de ventana de espíritu (árbol de pasivas)
    if (this.hudButtonSpirit) {
      this.hudButtonSpirit.on('pointerover', () => {
        this.hudButtonSpirit?.setTexture('hud_button_spirit_hover');
      });
      this.hudButtonSpirit.on('pointerout', () => {
        this.hudButtonSpirit?.setTexture('hud_button_spirit');
      });
      this.hudButtonSpirit.on('pointerup', (pointer: Phaser.Input.Pointer) => {
        this.scene.passiveTreeUi.togglePassiveTreePanel();
        pointer.event.stopPropagation();
      });
    }

    // Botón de magias (ventana a futuro)
    if (this.hudButtonSpells) {
      this.hudButtonSpells.on('pointerover', () => {
        this.hudButtonSpells?.setTexture('hud_button_spells_hover');
      });
      this.hudButtonSpells.on('pointerout', () => {
        this.hudButtonSpells?.setTexture('hud_button_spells');
      });
      this.hudButtonSpells.on('pointerup', (pointer: Phaser.Input.Pointer) => {
        this.scene.spellsPanel?.toggle();
        pointer.event.stopPropagation();
      });
    }

    // Botón de quests (abre/cierra el quest log, igual que la tecla 'L')
    if (this.hudButtonQuests) {
      this.hudButtonQuests.on('pointerover', () => {
        this.hudButtonQuests?.setTexture('hud_button_quests_hover');
      });
      this.hudButtonQuests.on('pointerout', () => {
        this.hudButtonQuests?.setTexture('hud_button_quests');
      });
      this.hudButtonQuests.on('pointerup', (pointer: Phaser.Input.Pointer) => {
        this.scene.questUI?.toggleLog();
        pointer.event.stopPropagation();
      });
    }

    // Ctrl+I → Icon Atlas Viewer (dev tool). Listener directo a Phaser keyboard
    // para no pasar por InputSystem (que reservó 'I' solo para Inventory).
    this.scene.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.scene.chatIsFocused) return;
      if (event.ctrlKey && event.key.toLowerCase() === 'i') {
        event.preventDefault();
        this.scene.toggleIconViewerPanel();
      }
    });
  }

}
